import { getUserDisplayName, type INotificationResource, type INotificationUser, NotificationType } from "@sol/sdk";

import { isFollowUpNotification, isSelfEvaluationNotification } from "./notificationTypeGroups";

export type NotificationMessageSegment =
    | { kind: "text"; i18nKey: string; values?: Record<string, string> }
    | { kind: "emphasizedText"; i18nKey: string; values: Record<string, string> }
    | { kind: "entityTitle"; title: string }
    | { kind: "plain"; text: string };

export type NotificationMessageSource = Pick<INotificationResource, "type"> & {
    titleEntity: Pick<INotificationResource["titleEntity"], "title">;
    contentUser?: Pick<INotificationUser, "name" | "surname" | "email">;
};

type MessageOptions = {
    isLearnerFollowUpWording: boolean;
};

const text = (i18nKey: string, values?: Record<string, string>): NotificationMessageSegment => ({
    kind: "text",
    i18nKey,
    ...(values ? { values } : {}),
});

type SentenceWordedType =
    | NotificationType.BRIEF_ASSIGNED
    | NotificationType.BRIEF_COEDITOR_ADDED_TO_TRAINER
    | NotificationType.SELF_EVALUATION_TO_TRAINER
    | NotificationType.SELF_EVALUATION_TO_TUTOR
    | NotificationType.FOLLOW_UP_TO_LEARNER
    | NotificationType.FOLLOW_UP_TO_TRAINER
    | NotificationType.FOLLOW_UP_TO_TUTOR;

type LearnerNamedType = NotificationType.WORK_CORRECTED_TO_TUTOR | NotificationType.GROUP_CORRECTION_TO_TUTOR;

// Typed over every remaining type so that a new notification type cannot ship without its wording
const ACTION_KEY_BEFORE_TITLE: Record<Exclude<NotificationType, SentenceWordedType | LearnerNamedType>, string> = {
    [NotificationType.WORK_NOT_CORRECTED]: "navbar.submitWork",
    [NotificationType.WORK_NOT_CORRECTED_TO_TUTOR]: "navbar.submitWork",
    [NotificationType.GROUP_WORK_TO_TRAINER]: "navbar.groupSubmitWork",
    [NotificationType.GROUP_WORK_TO_LEARNER]: "navbar.groupSubmitWork",
    [NotificationType.GROUP_WORK_TO_TUTOR]: "navbar.groupSubmitWork",
    [NotificationType.MESSAGE_LEARNER_TO_TRAINER]: "navbar.messageReceive",
    [NotificationType.MESSAGE_TRAINER_TO_LEARNER]: "navbar.messageReceive",
    [NotificationType.MESSAGE_TUTOR_TO_LEARNER]: "navbar.messageReceive",
    [NotificationType.MESSAGE_TRAINER_TO_TUTOR]: "navbar.messageReceive",
    [NotificationType.MESSAGE_LEARNER_TO_TUTOR]: "navbar.messageReceive",
    [NotificationType.GROUP_MESSAGE_LEARNER_TO_TRAINER]: "navbar.groupMessageReceive",
    [NotificationType.GROUP_MESSAGE_LEARNER_TO_LEARNER]: "navbar.groupMessageReceive",
    [NotificationType.GROUP_MESSAGE_TRAINER_TO_LEARNER]: "navbar.groupMessageReceive",
    [NotificationType.GROUP_MESSAGE_TUTOR_TO_LEARNER]: "navbar.groupMessageReceive",
    [NotificationType.GROUP_MESSAGE_TRAINER_TO_TUTOR]: "navbar.groupMessageReceive",
    [NotificationType.GROUP_MESSAGE_LEARNER_TO_TUTOR]: "navbar.groupMessageReceive",
    [NotificationType.WORK_CORRECTED]: "navbar.workCorrected",
    [NotificationType.GROUP_CORRECTION_TO_LEARNER]: "navbar.groupWorkCorrected",
    [NotificationType.GROUP_SPACE_TO_LEARNER]: "navbar.groupSpaceToLearner",
    [NotificationType.BRIEF_COEDITED_TO_CREATOR]: "navbar.briefCoeditedToCreator",
};

const ACTION_KEY_ABOUT_LEARNER: Record<LearnerNamedType, string> = {
    [NotificationType.WORK_CORRECTED_TO_TUTOR]: "navbar.workCorrectedToTutor",
    [NotificationType.GROUP_CORRECTION_TO_TUTOR]: "navbar.groupWorkCorrectedToTutor",
};

const findActionKey = (actionKeys: Partial<Record<NotificationType, string>>, type: NotificationType) =>
    actionKeys[type];

const getLearnerName = ({ contentUser }: NotificationMessageSource) =>
    contentUser ? getUserDisplayName(contentUser) : "";

const getLearnerNameSegments = (notification: NotificationMessageSource): NotificationMessageSegment[] => {
    const learnerName = getLearnerName(notification);

    return learnerName ? [{ kind: "plain", text: learnerName }] : [];
};

// Unknown types fall back to the work submission wording, as the API may send types the UI does not know yet
export const getNotificationMessage = (
    notification: NotificationMessageSource,
    { isLearnerFollowUpWording }: MessageOptions,
): NotificationMessageSegment[] => {
    const { type } = notification;
    const entityTitle: NotificationMessageSegment = { kind: "entityTitle", title: notification.titleEntity.title };

    if (type === NotificationType.BRIEF_ASSIGNED) {
        return [text("navbar.briefAssignedStart"), entityTitle, text("navbar.briefAssignedEnd")];
    }

    if (type === NotificationType.BRIEF_COEDITOR_ADDED_TO_TRAINER) {
        return [
            {
                kind: "emphasizedText",
                i18nKey: "navbar.briefSharedStart",
                values: { brief: notification.titleEntity.title },
            },
            { kind: "plain", text: "-" },
            text("navbar.briefSharedEnd"),
        ];
    }

    if (isSelfEvaluationNotification(type)) {
        return [text("navbar.selfEvaluation")];
    }

    if (isFollowUpNotification(type)) {
        return isLearnerFollowUpWording
            ? [text("navbar.toLearnerFollowUp"), entityTitle]
            : [text("navbar.followUpFile"), entityTitle, ...getLearnerNameSegments(notification)];
    }

    const actionAboutLearner = findActionKey(ACTION_KEY_ABOUT_LEARNER, type);

    if (actionAboutLearner) {
        return [text(actionAboutLearner, { learner: getLearnerName(notification) }), entityTitle];
    }

    return [text(findActionKey(ACTION_KEY_BEFORE_TITLE, type) ?? "navbar.submitWork"), entityTitle];
};
